import React, { useState } from 'react';
import { COLORS } from '../../utils/theme';

interface CredentialField {
  /** Label shown above the value (e.g. "USER ID (UUID)") */
  label: string;
  /** The credential value */
  value: string;
  /** Highlight the field with a warning background (e.g. for temporary passwords) */
  highlight?: boolean;
  /** Show a copy-to-clipboard button for this field */
  copyable?: boolean;
}

interface CredentialDisplayProps {
  /** Array of credential fields to display */
  fields: CredentialField[];
  /** Warning / help text shown below the fields */
  warning?: string;
  /** Extra style on the container */
  style?: React.CSSProperties;
}

const CopyButton: React.FC<{ value: string }> = ({ value }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback for older browsers
      const el = document.createElement('textarea');
      el.value = value;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <button
      onClick={handleCopy}
      title={copied ? 'Copied!' : 'Copy to clipboard'}
      style={{
        background: copied ? '#DCFCE7' : COLORS.neutral[100],
        border: `1px solid ${copied ? '#86EFAC' : COLORS.neutral[200]}`,
        borderRadius: 6,
        padding: '4px 10px',
        cursor: 'pointer',
        fontSize: 12,
        fontWeight: 600,
        color: copied ? '#16A34A' : COLORS.neutral[600],
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        flexShrink: 0,
        transition: 'all 0.15s ease',
        whiteSpace: 'nowrap',
      }}
    >
      {copied ? '✓ Copied' : '⎘ Copy'}
    </button>
  );
};

export const CredentialDisplay: React.FC<CredentialDisplayProps> = ({
  fields,
  warning,
  style,
}) => (
  <div
    style={{
      background: COLORS.white,
      border: `1.5px solid ${COLORS.neutral[200]}`,
      borderRadius: 16,
      padding: '24px 28px',
      boxShadow: '0 2px 12px rgba(27,58,107,0.07)',
      ...style,
    }}
  >
    <h3
      style={{
        margin: '0 0 20px',
        color: COLORS.neutral[800],
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 15,
        fontWeight: 700,
        letterSpacing: '-0.2px',
      }}
    >
      <span>🔐</span> Your Login Credentials
    </h3>

    <div style={{ display: 'grid', gap: 16 }}>
      {fields.map((field, i) => (
        <div key={i}>
          <label
            style={{
              color: COLORS.neutral[500],
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              display: 'block',
              marginBottom: 6,
            }}
          >
            {field.label}
          </label>
          <div
            style={{
              background: field.highlight ? '#FFFBEB' : COLORS.neutral[50],
              padding: '10px 12px 10px 16px',
              borderRadius: 8,
              border: `1.5px solid ${field.highlight ? '#F59E0B' : COLORS.neutral[200]}`,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <span
              style={{
                fontFamily: '"SFMono-Regular", "Consolas", "Liberation Mono", monospace',
                fontSize: 14,
                fontWeight: 500,
                wordBreak: 'break-all',
                color: COLORS.neutral[800],
                letterSpacing: field.highlight ? '0.04em' : 'normal',
                flex: 1,
              }}
            >
              {field.value}
            </span>
            {field.copyable && <CopyButton value={field.value} />}
          </div>
        </div>
      ))}
    </div>

    {warning && (
      <p
        style={{
          color: '#92400E',
          fontSize: 12,
          fontWeight: 500,
          marginTop: 14,
          marginBottom: 0,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 6,
          lineHeight: 1.5,
        }}
      >
        ⚠️ {warning}
      </p>
    )}
  </div>
);
